"use client";
import Link from "next/link";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useRouter, useSearchParams } from "next/navigation";
import { ArrowLeft, Menu } from "lucide-react";
import { useMenu } from "@/context/MenuProvider";

interface TopNavigationBarProps {
  title?: string;
  withBackButton?: boolean;
}

export function TopNavigationBarComponent({
  title,
  withBackButton,
}: TopNavigationBarProps) {
  const { toggleMenu } = useMenu();

  const searchParams = useSearchParams();
  const referer = searchParams.get("referer");
  const router = useRouter();

  return (
    <div className="flex justify-between items-center fixed z-50 top-0 md:left-60 bg-zinc-950 w-full md:w-[calc(100%-15rem)] pl-8 pr-2 md:pr-8 py-4">
      <div className="flex items-center gap-4 m-auto w-full">
        {withBackButton && (
          <button
            aria-label="back to previous page"
            onClick={() => {
              if (referer) {
                router.push(`/${referer.toLowerCase().split(" ").join("-")}`);
              } else {
                router.back();
              }
            }}
            className="capitalize hover:bg-accent p-2 rounded-full -ml-4">
            <ArrowLeft size={20} />
          </button>
        )}

        <div className="flex flex-col justify-center">
          <header>
            <Breadcrumb className="max-md:hidden" aria-label="breadcrumb">
              <BreadcrumbList itemScope itemType="https://schema.org/BreadcrumbList">
                <BreadcrumbItem
                  itemProp="itemListElement"
                  itemScope
                  itemType="https://schema.org/ListItem">
                  <Link href="/" itemProp="item">
                    <span itemProp="name">Home</span>
                    <span hidden itemProp="position" aria-hidden="true">
                      1
                    </span>
                  </Link>
                </BreadcrumbItem>
                <BreadcrumbSeparator>/</BreadcrumbSeparator>

                {referer && (
                  <>
                    <BreadcrumbItem
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem">
                      <Link
                        href={`/${referer.toLowerCase().split(" ").join("-")}`}
                        className="capitalize"
                        itemProp="item">
                        <span itemProp="name">{referer}</span>
                        <span hidden itemProp="position" aria-hidden="true">
                          2
                        </span>
                      </Link>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator>/</BreadcrumbSeparator>
                  </>
                )}

                <BreadcrumbItem
                  itemProp="itemListElement"
                  itemScope
                  itemType="https://schema.org/ListItem">
                  <BreadcrumbPage>
                    <span itemProp="name">{title}</span>
                    <span hidden itemProp="position" aria-hidden="true">
                      {referer ? "3" : "2"}
                    </span>
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </header>

          {title && <span className="text-xl font-extrabold">{title}</span>}
        </div>
      </div>
      <div className="flex items-center gap-4">
        <button
          className="block md:hidden p-2 px-4"
          onClick={() => toggleMenu()}
          aria-label="Toggle navigation">
          <Menu />
        </button>
      </div>
    </div>
  );
}
